/**
 * Efficiency: what an agent cost to reach a correct result (docs/efficiency-plan.md).
 *
 * Owner, 24 October 2026: EngineerOS sells efficiency with AI agents, the
 * same correct result for less money, and says only what it can prove. This
 * file is the shared contract: the units, the records the gateway meter keeps
 * (worker/ai-meter.ts), the study's records (worker/study.ts), and the pure
 * rules that turn a ledger into feedback. The price book is
 * ./price-book.ts.
 *
 * Four decisions shape it (the plan, "The decisions"):
 *
 * 1. **Dollars to a correct result, at a frozen price book.** An attempt's
 *    cost is its calls priced with `PRICE_BOOK`, so a provider changing
 *    prices never moves a before/after comparison. Only a pass has an
 *    efficiency figure; a failed or abandoned attempt is a failure, never a
 *    cheap success.
 * 2. **Metered only through our gateway.** Nothing self-reported counts.
 * 3. **By hand is allowed and never scored.** An attempt with no metered
 *    calls is `by: 'hand'` and has no ratio.
 * 4. **The credential shows the median ratio** over every metered checkpoint
 *    pass, once there are `CREDENTIAL_MIN_PASSES`.
 *
 * Money is in micro-dollars (1e-6 USD) throughout: one call often costs less
 * than a cent, and minor units would round it to nothing.
 */
import { PRICE_BOOKS, priceBookTier, priceCall } from './price-book.js';
/* ── Units and fixed numbers ────────────────────────────────────────────── */
/** The price-book edition every new call is priced with (./price-book.ts). */
export const PRICE_BOOK = 'pb-2026-10';
/** One US dollar in micro-dollars. */
export const MICRO_PER_USD = 1_000_000;
/** Practice credit included with every paid course period: $5, in USD minor units. */
export const PRACTICE_CREDIT_MINOR = 500;
/** Metered checkpoint passes before the public page shows an efficiency figure. */
export const CREDENTIAL_MIN_PASSES = 3;
/** A benchmark attempt stops counting at this multiple of par, or after `BENCH_CAP_MINUTES`. */
export const BENCH_CAP_PAR_MULTIPLE = 3;
export const BENCH_CAP_MINUTES = 60;
/** A paid task's reward is at least this multiple of its par. */
export const TASK_REWARD_PAR_MULTIPLE = 3;
/** Credit for finishing both study benchmarks: $10, in USD minor units. */
export const STUDY_CREDIT_MINOR = 1000;
/** The longest the study's learning stage lasts before the second benchmark opens. */
export const STUDY_LEARNING_DAYS = 21;
/* ── Feedback after a pass ──────────────────────────────────────────────── */
/** The techniques taught (docs/efficiency-techniques.md); each kept only while the lab shows it saves. */
/**
 * In teaching order. Owner, 25 October 2026, from the lab's Experiment 2
 * (docs/efficiency-par.md): the smallest model that still passes is the
 * measured saving, so it leads; the process rules (state the check first,
 * give it a test, review before accepting, don't stop at green) cost more
 * and caused the only failures when the agent could read the task itself,
 * so they are no longer taught as ways to save.
 */
export const TECHNIQUES = [
    'right-size-model',
    'scope-context',
    'restart',
    'stable-prefix',
    'exact-bug-report',
];
/** A call smaller than this is a tool's housekeeping (a title, a quota probe), not the agent's first turn. */
export const FIRST_CALL_MIN_INPUT = 1000;
/** Sum a run's calls into `RunUsage`, or null when there were none. */
export function runUsage(calls) {
    if (calls.length === 0)
        return null;
    const byModel = new Map();
    const usage = { inputTokens: 0, cachedTokens: 0, cacheWriteTokens: 0, outputTokens: 0, firstInputTokens: 0, models: [] };
    const ordered = [...calls].sort((a, b) => a.at - b.at);
    usage.firstInputTokens = (ordered.find((c) => c.inputTokens >= FIRST_CALL_MIN_INPUT) ?? ordered[0]).inputTokens;
    for (const call of calls) {
        usage.inputTokens += call.inputTokens;
        usage.cachedTokens += call.cachedTokens;
        usage.cacheWriteTokens += call.cacheWriteTokens;
        usage.outputTokens += call.outputTokens;
        const row = byModel.get(call.model) ?? { model: call.model, calls: 0, costMicro: 0 };
        row.calls += 1;
        row.costMicro += Math.max(0, call.bookCostMicro);
        byModel.set(call.model, row);
    }
    usage.models = [...byModel.values()].sort((a, b) => b.costMicro - a.costMicro || a.model.localeCompare(b.model)).slice(0, 6);
    return usage;
}
/**
 * Complete, chronological history through a passing level attempt. The first
 * pass is the baseline; ALL later retries, including earlier passing retries,
 * are charged to after. Never pick the cheapest replay or erase a failure.
 * The caller must supply complete history (the Worker suppresses overflow).
 * Reject hand passes, mixed source flags and different price books.
 */
export function practiceComparison(history) {
    const last = history.at(-1);
    if (!last || last.kind !== 'level' || last.outcome !== 'passed' || last.by !== 'agent')
        return null;
    if (new Set(history.map((a) => a.id)).size !== history.length)
        return null;
    for (const a of history) {
        if (a.kind !== 'level' || a.ref !== last.ref || a.book !== last.book || a.outcome === 'open' || a.endedAt === null)
            return null;
        if (!Number.isSafeInteger(a.costMicro) || a.costMicro < 0 || !Number.isSafeInteger(a.calls) || a.calls < 0)
            return null;
        if (a.calls === 0 && a.costMicro !== 0)
            return null;
        if (a.calls > 0 && Boolean(a.reported) !== Boolean(last.reported))
            return null;
        if (a.outcome === 'passed' && (a.by !== 'agent' || a.calls === 0))
            return null;
    }
    const firstPass = history.findIndex((a) => a.outcome === 'passed');
    const sum = (rows) => ({
        costMicro: rows.reduce((total, a) => total + a.costMicro, 0),
        calls: rows.reduce((total, a) => total + a.calls, 0),
        attempts: rows.length,
        passId: rows.at(-1).id,
    });
    const before = sum(history.slice(0, firstPass + 1));
    const after = firstPass < history.length - 1 ? sum(history.slice(firstPass + 1)) : null;
    if (before.costMicro <= 0 || !Number.isSafeInteger(before.costMicro) || (after && !Number.isSafeInteger(after.costMicro)))
        return null;
    return { book: last.book, reported: Boolean(last.reported), before, after };
}
/* ── The study (docs/efficiency-study.md) ───────────────────────────────── */
/** The benchmark's two parallel forms: four tasks each, the same kinds in the same order. */
export const BENCH_FORMS = {
    A: ['bench-a1', 'bench-a2', 'bench-a3', 'bench-a4'],
    B: ['bench-b1', 'bench-b2', 'bench-b3', 'bench-b4'],
};
/* ── Rules (pure; implemented and tested in server/test/efficiency.test.ts) ─ */
/** costMicro / parMicro for a passed agent attempt, else null. */
export function parRatio(attempt, parMicro) {
    if (attempt.outcome !== 'passed' || attempt.by !== 'agent' || !parMicro || parMicro <= 0)
        return null;
    return attempt.costMicro / parMicro;
}
/**
 * The passes among `attempts` that have a ratio to par (a pass, by an agent,
 * with par measured), summed, or null when there are none. Every such pass
 * counts, never only the best, as on the public page.
 */
export function parTotals(attempts) {
    let spentMicro = 0;
    let parMicro = 0;
    let passes = 0;
    for (const { attempt, parMicro: par } of attempts) {
        if (parRatio(attempt, par) === null)
            continue;
        spentMicro += attempt.costMicro;
        parMicro += par;
        passes += 1;
    }
    return passes === 0 ? null : { spentMicro, parMicro, passes };
}
/** The median of a non-empty list; null when empty. */
export function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}
/**
 * The public page's figure from every metered checkpoint pass with par, or
 * null below `CREDENTIAL_MIN_PASSES`. Every pass counts, never only the best.
 */
export function efficiencySummary(ratios, book = PRICE_BOOK) {
    if (ratios.length < CREDENTIAL_MIN_PASSES)
        return null;
    return { passes: ratios.length, medianRatio: median(ratios), book };
}
/**
 * The one technique a pass's ledger points to, or null when nothing stands
 * out. Rules, not a model: written in code, each with a test.
 *
 * Only a passed agent attempt is pointed anywhere, and never one at or under
 * par: a careful run's cost is not waste. Without a par the rules still run,
 * because each one rests on evidence in the ledger itself.
 *
 * Each rule estimates what its technique would have saved on this very
 * ledger, by re-pricing the calls it would have changed with the attempt's
 * own price book, and the biggest estimate wins. Nothing is pointed to when
 * the best estimate is under `MIN_SAVING_SHARE` of the attempt's cost. The
 * estimates are only for ranking; the lab measures what a technique saves
 * (docs/efficiency-techniques.md), and no figure from here is ever shown.
 */
export function pointTechnique(attempt, calls, parMicro, parModel = null) {
    if (attempt.outcome !== 'passed' || attempt.by !== 'agent' || calls.length === 0)
        return null;
    const ratio = parRatio(attempt, parMicro);
    if (ratio !== null && ratio <= 1)
        return null;
    const total = calls.reduce((sum, call) => sum + Math.max(0, call.bookCostMicro), 0);
    if (total <= 0)
        return null;
    const ordered = calls.map((call, index) => ({ call, index }))
        .sort((a, b) => a.call.at - b.call.at || a.index - b.index)
        .map(({ call }) => call);
    const sessions = splitSessions(mainThread(ordered));
    const book = attempt.book;
    const candidates = [
        { technique: 'right-size-model', saving: rightSizeSaving(book, ordered, parModel) },
        { technique: 'restart', saving: sum(sessions, (session) => restartSaving(book, session)) },
        { technique: 'scope-context', saving: sum(sessions, (session) => scopeSaving(book, session)) },
        { technique: 'stable-prefix', saving: sum(sessions, (session) => stablePrefixSaving(book, session)) },
    ];
    let best = candidates[0];
    for (const candidate of candidates)
        if (candidate.saving > best.saving)
            best = candidate;
    return best.saving >= MIN_SAVING_SHARE * total ? best.technique : null;
}
/* ── pointTechnique's rules ─────────────────────────────────────────────────
 *
 * What the ledger can see: for each call, when, which model, its input
 * tokens (the cached part among them) and its output tokens, and the cost.
 * Never the prompt, the answer, or a check's result. So four techniques can
 * be pointed to, each from a shape of the token counts:
 *
 * - `restart`: one session's input keeps growing, turn after turn;
 * - `scope-context`: one step adds a huge block of input that every later
 *   turn re-reads (a whole repository, a full log);
 * - `right-size-model`: most calls went to a model in a dearer tier than the
 *   one par was measured at (a small model passed this task in the lab), or,
 *   with no par model to go by, to a large-tier model;
 * - `stable-prefix`: long turns with little of their input read from cache.
 *
 * `exact-bug-report` is about what was asked. Nothing in the ledger shows
 * that, so it is taught as a lesson and never pointed to. Pointing to it from
 * a guess would be a model pretending to be a rule.
 *
 * When two estimates are equal, the first in the candidate list wins:
 * `right-size-model` first, because it is the saving the lab measured
 * (Experiment 2, docs/efficiency-par.md), then `restart`, and
 * `stable-prefix` last, because caching depends most on the learner's tool
 * rather than on the learner.
 *
 * Every threshold is a first setting from the 20 October agent runs and
 * Iris's costs, to be checked against the par lab's ledgers.
 */
/** The smallest estimated saving, as a share of the attempt's cost, worth pointing to: below a fifth, the advice is noise next to how much runs vary. */
const MIN_SAVING_SHARE = 0.2;
/** A call under this share of its session's peak input is a side call (a title, a subagent's turn), not the session going on. */
const RESTART_DROP_SHARE = 0.5;
/** That many side-sized calls in a row mean the context really was reset (a /clear, a compaction, a new session). */
const RESTART_CONFIRM_CALLS = 2;
/** A session shorter than this has not re-read its history often enough for a restart to pay. */
const RESTART_MIN_CALLS = 10;
/** A session whose peak input is less than this multiple of its start did not grow enough to point at. */
const RESTART_MIN_GROWTH = 2;
/** What a restart carries over: a short summary of where things stand. */
const RESTART_SUMMARY_TOKENS = 2_000;
/** About what an agent's own system prompt and tool definitions take before any work (Claude Code and Codex send roughly 10-30k). */
const SESSION_BASE_TOKENS = 30_000;
/** One step adding this much input read far more than the step needed: a few named files rarely come to 40k tokens. */
const SCOPE_JUMP_TOKENS = 40_000;
/** What a scoped read of the same step is allowed to have added instead. */
const SCOPE_ALLOWANCE_TOKENS = 10_000;
/** Turns this long are where a cache miss costs real money. */
const STABLE_PREFIX_MIN_INPUT = 20_000;
/** Fewer long turns than this and caching has too little to work on. */
const STABLE_PREFIX_MIN_CALLS = 5;
/** Under this share of long turns' input read from cache, the prefix kept changing (or the route kept changing provider). */
const STABLE_PREFIX_MAX_CACHED_SHARE = 0.5;
/** With a stable prefix, about this much of the previous turn's input is a cache hit on the next. */
const STABLE_PREFIX_REACHABLE = 0.9;
/** At least this share of calls on a dearer model than the job needed: big models for the mechanical steps too, not only for design. */
const RIGHT_SIZE_MIN_CALL_SHARE = 0.5;
function sum(items, value) {
    return items.reduce((total, item) => total + value(item), 0);
}
/**
 * What a change to one call's tokens would have saved, priced with the
 * attempt's book both ways (so a stored cost that disagrees with the book
 * never makes a saving out of nothing). A model the book cannot price saves
 * nothing: the estimate stays conservative rather than guessing its prices.
 */
function callSaving(book, call, changed, model = call.model) {
    const before = priceCall(book, call.model, call);
    const after = priceCall(book, model, changed);
    return before === null || after === null ? 0 : Math.max(0, before - after);
}
/** The same call with `cut` input tokens fewer, taken from the cached part first: what is removed is old history, which is what the cache holds. */
function withoutCarried(call, cut) {
    const fromCache = Math.min(call.cachedTokens, cut);
    return {
        inputTokens: Math.max(0, call.inputTokens - cut),
        cachedTokens: call.cachedTokens - fromCache,
        // Kept, not cut: the estimate stays conservative (priceCall clamps it to the input left).
        cacheWriteTokens: call.cacheWriteTokens,
        outputTokens: call.outputTokens,
    };
}
/**
 * The agent's main loop: the calls on the model that cost the most. Agents
 * send side work (titles, summaries, subagents) to other models, and those
 * calls would break the turn-by-turn shape the context rules read. Ties go
 * to the alphabetically first model, so the choice is deterministic.
 */
function mainThread(ordered) {
    const byModel = new Map();
    for (const call of ordered)
        byModel.set(call.model, (byModel.get(call.model) ?? 0) + Math.max(0, call.bookCostMicro));
    let main = null;
    for (const [model, cost] of byModel) {
        if (main === null || cost > byModel.get(main) || (cost === byModel.get(main) && model < main))
            main = model;
    }
    return ordered.filter((call) => call.model === main);
}
/**
 * The main loop cut into sessions. A session ends when its input drops
 * under `RESTART_DROP_SHARE` of its peak for `RESTART_CONFIRM_CALLS` calls
 * in a row; a single small call inside a session is a side call and is left
 * out of the session's shape.
 */
function splitSessions(main) {
    const sessions = [];
    let current = [];
    let peak = 0;
    let dropped = [];
    for (const call of main) {
        if (current.length > 0 && call.inputTokens < RESTART_DROP_SHARE * peak) {
            dropped.push(call);
            if (dropped.length >= RESTART_CONFIRM_CALLS) {
                sessions.push(current);
                current = dropped;
                peak = Math.max(...dropped.map((c) => c.inputTokens));
                dropped = [];
            }
            continue;
        }
        dropped = [];
        current.push(call);
        peak = Math.max(peak, call.inputTokens);
    }
    if (current.length > 0)
        sessions.push(current);
    return sessions;
}
/**
 * `restart`: had the agent restarted halfway through a long, growing
 * session with a short summary, every later turn would have carried that
 * much less history.
 */
function restartSaving(book, session) {
    if (session.length < RESTART_MIN_CALLS)
        return 0;
    const start = median(session.slice(0, 3).map((call) => call.inputTokens));
    const peak = Math.max(...session.map((call) => call.inputTokens));
    if (start <= 0 || peak < RESTART_MIN_GROWTH * start)
        return 0;
    const half = Math.floor(session.length / 2);
    const carried = session[half].inputTokens - start - RESTART_SUMMARY_TOKENS;
    if (carried <= 0)
        return 0;
    let saving = 0;
    for (const call of session.slice(half + 1)) {
        const cut = Math.min(carried, Math.max(0, call.inputTokens - start));
        saving += callSaving(book, call, withoutCarried(call, cut));
    }
    return saving;
}
/**
 * `scope-context`: each step that added more than `SCOPE_JUMP_TOKENS` is
 * taken to have needed only `SCOPE_ALLOWANCE_TOKENS`; the excess is gone
 * from that turn (uncached, it was just read) and from every later turn of
 * the session (cached by then). A session's first call is measured against
 * `SESSION_BASE_TOKENS`, so a huge opening prompt counts too.
 */
function scopeSaving(book, session) {
    let previous = SESSION_BASE_TOKENS;
    let carried = 0;
    let saving = 0;
    for (const call of session) {
        const added = call.inputTokens - previous;
        const excess = added >= SCOPE_JUMP_TOKENS ? added - SCOPE_ALLOWANCE_TOKENS : 0;
        const room = Math.max(0, call.inputTokens - SESSION_BASE_TOKENS);
        const fromCache = Math.min(call.cachedTokens, carried, room);
        const fromFresh = Math.min(call.inputTokens - call.cachedTokens, Math.min(carried + excess, room) - fromCache);
        if (fromCache + fromFresh > 0) {
            saving += callSaving(book, call, {
                inputTokens: call.inputTokens - fromCache - fromFresh,
                cachedTokens: call.cachedTokens - fromCache,
                cacheWriteTokens: call.cacheWriteTokens,
                outputTokens: call.outputTokens,
            });
        }
        carried += excess;
        previous = call.inputTokens;
    }
    return saving;
}
/**
 * `stable-prefix`: long turns that read little from cache. With a stable
 * prefix, most of the previous turn's input would have been a cache hit.
 * A model the book gives no cache discount saves nothing here.
 */
function stablePrefixSaving(book, session) {
    const long = session.map((call, i) => ({ call, previous: session[i - 1] }))
        .filter(({ call, previous }) => previous && call.inputTokens >= STABLE_PREFIX_MIN_INPUT);
    if (long.length < STABLE_PREFIX_MIN_CALLS)
        return 0;
    const input = sum(long, ({ call }) => call.inputTokens);
    const cached = sum(long, ({ call }) => Math.min(call.cachedTokens, call.inputTokens));
    if (input <= 0 || cached / input >= STABLE_PREFIX_MAX_CACHED_SHARE)
        return 0;
    return sum(long, ({ call, previous }) => {
        const reachable = Math.floor(Math.min(previous.inputTokens, call.inputTokens) * STABLE_PREFIX_REACHABLE);
        return callSaving(book, call, { ...call, cachedTokens: Math.max(call.cachedTokens, reachable) });
    });
}
/**
 * `right-size-model`: most calls on a dearer model than the job needed.
 *
 * With `parModel` (the model par was measured on, which passed every lab run
 * of this task), a call in a dearer tier than that model's is re-priced on
 * that very model: the lab showed it reaches the correct result, so a step
 * down from mid to small is pointed to as well. Owner, 25 October 2026: on
 * the benchmark tasks Haiku 5.5 reached a correct result for a tenth of
 * Sonnet's cost (Experiment 2, docs/efficiency-par.md).
 *
 * Without one, only large-tier calls count, re-priced on every mid-tier model
 * of the same provider (of the whole book if it has none) and the median of
 * those costs taken: not the cheapest mid, which would flatter the advice,
 * and not the dearest, which can be an old model dearer on cached input than
 * a new large one. A step to small is then not pointed to: nothing says a
 * small model would still have passed.
 */
function rightSizeSaving(book, ordered, parModel) {
    const target = parModel ? priceBookTier(book, parModel) : null;
    const above = target
        ? ordered.filter((call) => TIER_RANK[priceBookTier(book, call.model) ?? target] > TIER_RANK[target])
        : ordered.filter((call) => priceBookTier(book, call.model) === 'large');
    if (above.length === 0 || above.length < RIGHT_SIZE_MIN_CALL_SHARE * ordered.length)
        return 0;
    return sum(above, (call) => {
        const before = priceCall(book, call.model, call);
        const after = target ? priceCall(book, parModel, call) : median(midModels(book, call.model.split('/')[0]).map((mid) => priceCall(book, mid, call)));
        return before === null || after === null ? 0 : Math.max(0, before - after);
    });
}
/** Tiers from cheapest to dearest. */
const TIER_RANK = { small: 0, mid: 1, large: 2 };
/** The book's mid-tier models from `provider`, or every mid-tier model when the provider has none. */
function midModels(edition, provider) {
    const models = Object.hasOwn(PRICE_BOOKS, edition) ? PRICE_BOOKS[edition].models : {};
    const mids = Object.keys(models).filter((id) => models[id].tier === 'mid');
    const own = mids.filter((id) => id.startsWith(`${provider}/`));
    return own.length > 0 ? own : mids;
}
//# sourceMappingURL=efficiency.js.map